import { type FC, type ReactNode, useMemo } from "react";
import { useInView } from "react-intersection-observer";

import { type TBTStatusBadgeDisplayTypes } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";
import { useIsMobileViewportWebview } from "@btnet/utilities-webview/useIsMobileViewportWebview";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isInIframe } from "utilities/url/url";

import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    EntityCopyLinkModalContent,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";

import "./BTModalLayoutBDS.less";

/**
 * Container-query utilities that flip the header row into a stack. Exported so the test
 * asserts one source of truth (same pattern as `Frame.tsx`'s `FRAME_SLOT_PADDING`).
 * Must stay literal source text — Tailwind v4 only emits classes it can find verbatim.
 */
export const STACKED_HEADER_LAYOUT_CLASS =
    "@max-sm/bt-modal-layout-bds:flex-col @max-sm/bt-modal-layout-bds:items-start";

export const STACKED_HEADER_TEXT_WRAP = "@max-sm/bt-modal-layout-bds:whitespace-normal";

interface IBTModalLayoutBDSBaseProps {
    /**
     * A boolean to determine if the modal should use the BDS layout or the legacy layout.
     * @default false
     */
    useBDSLayout?: boolean;
    /**
     * The header for the modal layout component/ use this to override the default header.
     * @default undefined
     */
    header?: ReactNode;
    /**
     * The title of the modal
     */
    title?: string;
    /**
     * The subtitle of the modal
     */
    subtitle?: string;
    /**
     * The sharing configuration for the modal
     */
    sharing?: ISharingConfig;
    /**
     * The footer content for the modal
     */
    footerContent?: ReactNode;
    /**
     * The configuration for the modal
     */
    modalConfig?: IModalConfiguration;
    /**
     * The class name for the modal
     */
    className?: string;
    /**
     * Controls whether the close button and click-outside-to-close mask are enabled.
     * The mask is hidden in any webview context regardless of this value, and the close button is
     * hidden in a phone-width webview where native back is the dismissal affordance.
     * @default true
     */
    closable?: boolean;
    /**
     * The children nodes
     */
    children: ReactNode;
    /**
     * A boolean to determine if the BDS layout content should have padding
     * @default true
     */
    useBDSLayoutContentPadding?: boolean;
    /**
     * The status message to be displayed in status badge of the header.
     * @default undefined
     */
    statusMessage?: string;
    /**
     * The test id for the status badge. This is required if you want to display a status badge.
     * @default undefined
     */
    statusTestId?: string;
    /**
     * The type of status badge to be displayed.
     * @default undefined
     */
    statusType?: TBTStatusBadgeDisplayTypes;
    layoutRef?: React.RefObject<HTMLDivElement>;
    headerRef?: React.RefObject<HTMLDivElement>;
    /**
     * Optional array of icon action configurations for the header.
     */
    iconActions?: TBTItemHeaderIcon[];
    /**
     * Opt in to stacking the header onto separate lines — job name, title, status, then
     * actions — when the `bt-modal-layout-bds` container is narrower than 576px.
     *
     * Only takes effect on the `useBDSLayout` path. The legacy `.BTModalLayoutBDS` branch
     * establishes no container, so the query never matches and the prop is inert there.
     * @default false
     */
    stackHeaderOnNarrowWidth?: boolean;
}

export type IBTModalLayoutBDSProps = IBTModalLayoutBDSBaseProps & {
    /**
     * Test id for the layout root, and the stem of the generated header's test id.
     * @default undefined
     */
    testId?: string;
};

/**
 * Modal layout with a BTItemHeader header, scrolling content, and an optional footer.
 *
 * @param useBDSLayout - A boolean to determine if the modal should use the BDS layout or the legacy layout.
 * @param title - The title of the modal.
 * @param subtitle - The subtitle of the modal.
 * @param sharing - The sharing configuration for the modal.
 * @param footerContent - The footer content for the modal.
 * @param modalConfig - The modal configuration for the modal.
 * @param className - The class name for the modal.
 * @param closable - A boolean to determine if the modal is closable.
 * @param useBDSContentPadding - A boolean to determine if the BDS layout content should have padding.
 *
 */

export const BTModalLayoutBDS: FC<IBTModalLayoutBDSProps & ITrackingProp> = track({})((props) => {
    const {
        useBDSLayout = false,
        header,
        className,
        closable,
        title,
        subtitle,
        sharing,
        modalConfig,
        footerContent,
        children,
        tracking,
        useBDSLayoutContentPadding = true,
        statusMessage,
        statusTestId,
        statusType,
        layoutRef,
        headerRef,
        testId,
        iconActions,
        stackHeaderOnNarrowWidth = false,
    } = props;
    const webviewContext = useWebviewContext();
    // Width-aware, unlike the mask's `webviewContext` check: a tablet-width webview still renders
    // this as a floating card with room for its own X, and dropping it there would leave no way out.
    const isMobileViewportWebview = useIsMobileViewportWebview();
    const { ref: bottomRef, inView: bottomInView } = useInView();

    const defaultCloseable = closable ?? true;
    const modalMask = (
        <div
            className="BTModalMaskBDS"
            onClick={() => {
                modalConfig?.beforeClose();
                tracking?.trackEvent({
                    event: "ModalClose",
                    extraInfo: {
                        isFromClickOut: true,
                    },
                });
            }}
        />
    );

    const beforeClose = modalConfig?.beforeClose;

    const modalIconActions = useMemo<TBTItemHeaderIcon[]>(() => {
        const actions: TBTItemHeaderIcon[] = [];

        if (sharing) {
            actions.push({
                icon: "share",
                popoverContent: <EntityCopyLinkModalContent sharing={sharing} />,
                inMenuMdBreakpoint: true,
                onClick: () => {},
            });
        }

        if (iconActions) {
            actions.push(...iconActions);
        }

        if (defaultCloseable && !isMobileViewportWebview) {
            actions.push({
                icon: "closeX",
                onClick: () => beforeClose?.(),
                pinned: true,
            });
        }

        return actions;
    }, [beforeClose, defaultCloseable, iconActions, isMobileViewportWebview, sharing]);

    const modalItemHeader = (
        <div ref={headerRef}>
            <BTItemHeader
                className="p-4 bt-modal-item-header"
                testid={`${testId ?? "bt"}-modal-item-header`}
                ariaLabel="header"
            >
                <div
                    data-testid="modalItemHeaderLayout"
                    className={cn("flex flex-row items-center", {
                        [STACKED_HEADER_LAYOUT_CLASS]: stackHeaderOnNarrowWidth,
                    })}
                >
                    <div className="flex max-w-full min-w-0 flex-col">
                        {subtitle && (
                            <BTText
                                tag="h1"
                                size="distinct-sm"
                                className={cn("truncate", {
                                    [STACKED_HEADER_TEXT_WRAP]: stackHeaderOnNarrowWidth,
                                })}
                            >
                                {subtitle}
                            </BTText>
                        )}
                        <div
                            data-testid="modalItemHeaderTitleRow"
                            className={cn("flex flex-row flex-wrap items-center gap-x-2 gap-y-1", {
                                [STACKED_HEADER_LAYOUT_CLASS]: stackHeaderOnNarrowWidth,
                            })}
                        >
                            {title && (
                                <BTText
                                    testid="modalTitle"
                                    tag="h2"
                                    size="distinct-lg"
                                    className={cn("whitespace-nowrap", {
                                        [STACKED_HEADER_TEXT_WRAP]: stackHeaderOnNarrowWidth,
                                    })}
                                >
                                    {title}
                                </BTText>
                            )}
                            {statusMessage && statusType && statusTestId && (
                                // Metadata's own top/bottom spacing assumes it sits under the
                                // title; inline, the row's gap spaces it instead.
                                <BTItemHeader.Metadata
                                    badgeText={statusMessage}
                                    badgeDisplayType={statusType}
                                    testid={statusTestId}
                                    className="pt-0 md:pt-0 mb-0"
                                />
                            )}
                        </div>
                    </div>
                    {/* ActionButtons' `width: 100%` basis would otherwise squeeze the title column
                        and wrap the status pill even when there's room; `flex-1` makes it take only
                        the space left over. */}
                    <BTItemHeader.ActionButtons
                        testid="bt-item-header-action-buttons"
                        iconActions={modalIconActions}
                        className="flex-1"
                    />
                </div>
            </BTItemHeader>
        </div>
    );

    // A layout with nothing to put in a header renders none, rather than an empty padded band
    // with a duplicate close affordance — some consumers (e.g. CopyRolePermissions) supply their
    // title through an outer BTModalLayout and pass none here.
    const headerContent = header ?? (title || subtitle ? modalItemHeader : null);

    const model = (
        <>
            {useBDSLayout ? (
                <>
                    <div
                        data-testid={testId ?? "modalLayout"}
                        className={cn("bds bt-modal-layout-bds flex flex-col w-full", className, {
                            "bt-modal-layout-bds--footer-shadow": !bottomInView,
                        })}
                    >
                        {headerContent && (
                            <div className="bt-modal-layout-bds-header">{headerContent}</div>
                        )}

                        <div
                            className={cn("bt-modal-layout-bds-content flex-1 overflow-y-auto", {
                                "bt-modal-layout-bds-content--no-padding":
                                    !useBDSLayoutContentPadding,
                            })}
                            data-testid="modalContent"
                            ref={layoutRef}
                        >
                            {children}
                            <div ref={bottomRef} />
                        </div>

                        {footerContent && (
                            <div
                                data-testid="modalFooter"
                                className="flex flex-row bt-modal-layout-bds-footer items-center"
                            >
                                {footerContent}
                            </div>
                        )}
                    </div>
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </>
            ) : (
                <div
                    className={cn("BTModalLayoutBDS", className)}
                    data-testid={testId ?? "modalLayout"}
                    ref={layoutRef}
                >
                    {headerContent}
                    <div className="ModalContentContainer">
                        {children}
                        <div ref={bottomRef} />
                    </div>
                    {footerContent && (
                        <div
                            className={cn("BTModalFooterBDS", {
                                FooterShadow: !bottomInView,
                            })}
                            data-testid="modalFooter"
                        >
                            {footerContent}
                        </div>
                    )}
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </div>
            )}
        </>
    );
    return isInIframe() ? <FocusProvider>{model}</FocusProvider> : model;
});
